import { useEffect, useRef, useState } from 'react';
import { Box, Button, InputBase, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import useIsMobile from 'app/hooks/useIsMobile';
import { sentryMask } from 'sentryReplayPrivacy';

export interface StepUpOtpModalProps {
  open: boolean;
  /** Title above the code boxes. Defaults to "Verification code". */
  title?: string;
  /**
   * Subtitle under the title. Defaults to "Enter the 6 digit code sent to phone
   * number ending in {last4}". Override for non-phone destinations (e.g. email).
   */
  subtitle?: React.ReactNode;
  /** Masked destination phone, e.g. "(•••) •••-7715"; the last 4 digits are shown. */
  maskedPhone?: string;
  /** True while a code is being sent or verified. */
  loading?: boolean;
  /** Hard error to surface (e.g. "We could not send a code."). */
  error?: string | null;
  /** Remaining verify attempts; shown as "N attempts remaining" (defaults to the full cap). */
  attemptsRemaining?: number | null;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onClose: () => void;
}

const CODE_LENGTH = 6;
const MAX_ATTEMPTS = 3;
const RESEND_SECONDS = 25;

// Design tokens — Figma "OTP - authentication - bank" (node 4255:34011).
const COLOR_TITLE = '#3A3B41';
const COLOR_SECONDARY = '#777779';
const COLOR_BORDER = '#DEE3EB';
const COLOR_FOCUS = '#193059';
const COLOR_PRIMARY_HOVER = '#142747';
const COLOR_ERROR = '#D32F2F';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/** Speedchain loading spinner (Figma Speedchain_LoadingSpinner / Spin 1 / dark). */
function VerificationSpinner(): JSX.Element {
  return (
    <Box
      role="status"
      aria-label="Verifying code"
      sx={{
        display: 'flex',
        width: 40,
        height: 40,
        animation: `${spin} 0.9s linear infinite`,
        // Respect reduced-motion: slow the spin rather than stopping it (it still
        // needs to read as "working").
        '@media (prefers-reduced-motion: reduce)': { animationDuration: '2.4s' },
      }}
    >
      <svg width="40" height="40" viewBox="0 0 41 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M22.7893 1.97846C23.1214 0.925011 24.2447 0.340286 25.2981 0.672436L26.879 1.17089L28.4911 1.83865L30.0389 2.64438L31.5106 3.58193L32.895 4.64419L34.1815 5.82305L35.3603 7.10956L36.4226 8.49391L37.3601 9.96558L38.1255 11.4359C38.6356 12.4157 38.2548 13.6234 37.275 14.1334C36.2952 14.6435 35.0875 14.2627 34.5775 13.2829L33.8927 11.9675L33.1428 10.7904L32.2932 9.68315L31.3503 8.65418L30.3214 7.71128L29.2141 6.86166L28.037 6.11178L26.7991 5.46734L25.5096 4.93324L24.0953 4.4873C23.0419 4.15515 22.4571 3.0319 22.7893 1.97846Z"
          fill="#D8E2FF"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M18.8654 1.80674C19.1045 2.88512 18.4241 3.95314 17.3457 4.19221L15.8979 4.51319L14.5668 4.93287L13.2774 5.46696L12.0394 6.1114L10.8623 6.86128L9.7551 7.71091L8.72612 8.65379L7.78323 9.68278L6.93361 10.79L6.18372 11.9671L5.53928 13.2051L5.00519 14.4945L4.5855 15.8255L4.28343 17.1881L4.10126 18.5718L4.04038 19.9662L4.10126 21.3605L4.28343 22.7442L4.58551 24.1068L5.00518 25.4378L5.53928 26.7273L6.18372 27.9652L6.93361 29.1423L7.78323 30.2495L8.72612 31.2785L9.7551 32.2214L10.8623 33.071L12.0394 33.8209L13.2774 34.4654L14.5668 34.9994L15.8979 35.4191L17.2604 35.7212L18.6441 35.9034L20.0385 35.9642L21.4328 35.9034L22.8165 35.7212L24.1791 35.4191L25.5101 34.9994L26.7996 34.4654L28.0375 33.8209L29.2146 33.071L30.3218 32.2214L31.3508 31.2785L32.2937 30.2495L33.1433 29.1423L33.8932 27.9652L34.5377 26.7272L35.0718 25.4379L35.4915 24.1068L35.7935 22.7442L35.9757 21.3605L36.0404 19.8789C36.0886 18.7754 37.0222 17.9199 38.1257 17.9681C39.2292 18.0162 40.0848 18.9499 40.0366 20.0534L39.9643 21.7094L39.7365 23.4394L39.3588 25.143L38.8341 26.8072L38.1664 28.4193L37.3606 29.9671L36.4231 31.4388L35.3608 32.8231L34.182 34.1096L32.8955 35.2885L31.5111 36.3508L30.0394 37.2883L28.4916 38.094L26.8795 38.7618L25.2154 39.2865L23.5118 39.6642L21.7818 39.8919L20.0385 39.9681L18.2952 39.8919L16.5652 39.6642L14.8616 39.2865L13.1974 38.7618L11.5853 38.094L10.0375 37.2883L8.56586 36.3508L7.1815 35.2885L5.89499 34.1096L4.71613 32.8231L3.65388 31.4388L2.71632 29.9671L1.9106 28.4193L1.24284 26.8072L0.718122 25.143L0.340447 23.4394L0.112686 21.7094L0.0365728 19.9662L0.112686 18.2229L0.340447 16.4929L0.718121 14.7893L1.24284 13.1251L1.9106 11.513L2.71632 9.9652L3.65388 8.49354L4.71613 7.10918L5.89499 5.82267L7.1815 4.64381L8.56586 3.58156L10.0375 2.644L11.5853 1.83827L13.1974 1.17051L14.8616 0.645801L16.4799 0.287027C17.5583 0.0479535 18.6263 0.728352 18.8654 1.80674Z"
          fill="#193059"
        />
      </svg>
    </Box>
  );
}

const lastFourDigits = (phone?: string): string | null => {
  const digits = (phone || '').replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : null;
};

const formatCountdown = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/**
 * SEE-221: SMS step-up challenge shown before bank linking for single-factor
 * (password / SSO) sessions. Matches the Figma "Verification code" design:
 * six digit boxes, auto-submit on the last digit, an attempts counter, a resend
 * countdown, and a spinner while the code is validated.
 */
function StepUpOtpModal({
  open,
  title,
  subtitle,
  maskedPhone,
  loading = false,
  error,
  attemptsRemaining,
  onSubmit,
  onResend,
  onClose,
}: Readonly<StepUpOtpModalProps>): JSX.Element {
  const [digits, setDigits] = useState<string[]>(() => Array(CODE_LENGTH).fill(''));
  const [resendIn, setResendIn] = useState(RESEND_SECONDS);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const lastFailure = useRef<string>('');
  const isMobile = useIsMobile();

  const allFilled = digits.every((d) => d !== '');
  // Spinner/disabled is the validation step only — not the initial send — so the
  // user can start typing the moment the code arrives.
  const verifying = loading && allFilled;
  const last4 = lastFourDigits(maskedPhone);

  const focusBox = (i: number) => inputs.current[Math.min(Math.max(i, 0), CODE_LENGTH - 1)]?.focus();

  // (Re)open: clear the code, restart the resend countdown, focus the first box.
  useEffect(() => {
    if (!open) return undefined;
    setDigits(Array(CODE_LENGTH).fill(''));
    setResendIn(RESEND_SECONDS);
    const id = window.setTimeout(() => focusBox(0), 60);
    return () => window.clearTimeout(id);
  }, [open]);

  // Resend countdown ticks once a second while open, flooring at 0.
  useEffect(() => {
    if (!open) return undefined;
    const id = window.setInterval(() => setResendIn((s) => (s <= 0 ? 0 : s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  // A failed attempt — a hard error, or a wrong code (which decrements
  // attemptsRemaining) — clears the boxes so the user can re-enter.
  useEffect(() => {
    const token = `${error ?? ''}|${attemptsRemaining ?? ''}`;
    const isFailure = Boolean(error) || typeof attemptsRemaining === 'number';
    if (open && isFailure && token !== lastFailure.current) {
      setDigits(Array(CODE_LENGTH).fill(''));
      const id = window.setTimeout(() => focusBox(0), 0);
      lastFailure.current = token;
      return () => window.clearTimeout(id);
    }
    lastFailure.current = token;
    return undefined;
  }, [error, attemptsRemaining, open]);

  const commit = (next: string[]) => {
    setDigits(next);
    if (next.every((d) => d !== '')) onSubmit(next.join(''));
  };

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    if (digit && index < CODE_LENGTH - 1) focusBox(index + 1);
    commit(next);
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      setDigits(next);
      focusBox(index - 1);
    } else if (event.key === 'ArrowLeft' && index > 0) {
      focusBox(index - 1);
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      focusBox(index + 1);
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    commit(Array.from({ length: CODE_LENGTH }, (_, i) => pasted[i] ?? ''));
    focusBox(pasted.length - 1);
  };

  const resend = () => {
    onResend();
    setResendIn(RESEND_SECONDS);
    setDigits(Array(CODE_LENGTH).fill(''));
    focusBox(0);
  };

  const hasError = Boolean(error);
  // Attempts are shown from the start (defaulting to the full cap), counting
  // down as codes are missed: "3 attempts remaining" → "1 attempt remaining".
  const attemptsLeft = attemptsRemaining ?? MAX_ATTEMPTS;
  const locked = attemptsLeft === 0; // 3 wrong tries used — the code is dead
  const attemptsLabel = locked
    ? 'No attempts remaining'
    : `${attemptsLeft} attempt${attemptsLeft === 1 ? '' : 's'} remaining`;
  // Standard OTP behavior: the resend cooldown rolls independently of wrong
  // tries. A wrong code just clears the boxes for re-entry; Resend stays gated
  // until the countdown elapses OR every attempt is spent (code locked).
  const showResendButton = resendIn === 0 || locked;

  return (
    <ResponsiveDialog
      open={open}
      onClose={verifying ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: { xs: '100%', sm: 558 }, maxWidth: '100%', borderRadius: '8px' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '40px', p: { xs: '32px 24px', sm: '40px' } }}>
        <Typography
          component="h2"
          sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 700, color: COLOR_TITLE, textAlign: 'center' }}
        >
          {title ?? 'Verification code'}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {/* The destination: phone last-4, or a caller's subtitle (e.g. a masked email). */}
          <Typography
            {...sentryMask}
            sx={{
              fontSize: 14,
              lineHeight: '20px',
              letterSpacing: '0.25px',
              color: COLOR_SECONDARY,
              textAlign: 'center',
            }}
          >
            {subtitle ??
              `Enter the 6 digit code sent to ${last4 ? `phone number ending in ${last4}` : 'your phone'}`}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              {digits.map((digit, index) => (
                <InputBase
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  value={digit}
                  disabled={verifying || locked}
                  inputRef={(el: HTMLInputElement | null) => {
                    inputs.current[index] = el;
                  }}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  onFocus={(e) => e.target.select()}
                  inputProps={{
                    ...sentryMask,
                    inputMode: 'numeric',
                    autoComplete: index === 0 ? 'one-time-code' : 'off',
                    maxLength: 1,
                    'aria-label': `Digit ${index + 1} of ${CODE_LENGTH}`,
                    style: { textAlign: 'center', padding: 0 },
                  }}
                  sx={{
                    width: 40,
                    height: 40,
                    border: '1px solid',
                    borderColor: hasError ? COLOR_ERROR : digit ? COLOR_TITLE : COLOR_BORDER,
                    borderRadius: '8px',
                    fontSize: 18,
                    fontWeight: 600,
                    color: COLOR_TITLE,
                    transition: 'border-color 120ms ease, box-shadow 120ms ease',
                    '&.Mui-focused': {
                      borderColor: hasError ? COLOR_ERROR : COLOR_FOCUS,
                      boxShadow: `0 0 0 1px ${hasError ? COLOR_ERROR : COLOR_FOCUS}`,
                    },
                  }}
                />
              ))}
            </Box>

            <Box
              sx={{
                minHeight: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  lineHeight: '16px',
                  color: hasError ? COLOR_ERROR : COLOR_SECONDARY,
                  textAlign: 'center',
                }}
              >
                {hasError ? error : attemptsLabel}
              </Typography>
            </Box>

            <Box
              sx={{
                minHeight: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              {verifying ? (
                <VerificationSpinner />
              ) : showResendButton ? (
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  disabled={loading}
                  onClick={resend}
                  sx={{
                    bgcolor: COLOR_FOCUS,
                    color: '#fff',
                    textTransform: 'none',
                    fontSize: 14,
                    fontWeight: 600,
                    borderRadius: '8px',
                    height: 48,
                    '&:hover': { bgcolor: COLOR_PRIMARY_HOVER },
                  }}
                >
                  Resend code
                </Button>
              ) : (
                <Typography
                  sx={{ fontSize: 12, lineHeight: '16px', color: COLOR_SECONDARY, textAlign: 'center' }}
                >
                  Resend code in <strong>{formatCountdown(resendIn)}</strong>
                </Typography>
              )}
            </Box>

            {/* Desktop dismisses via the backdrop/Esc (per the design); mobile is
                fullscreen with no backdrop, so give it an explicit way out. */}
            {isMobile && (
              <Button
                variant="text"
                onClick={onClose}
                disabled={verifying}
                sx={{ color: COLOR_SECONDARY, textTransform: 'none', fontSize: 14, fontWeight: 600 }}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </ResponsiveDialog>
  );
}

export default StepUpOtpModal;
